import { useState, useEffect, useCallback, useMemo } from 'react';
import { OperatingCost, CostInterval, calculateYearlyCost } from '../../types/operatingCosts';
import { FileAttachment } from '../../types/attachments';
import { operatingCostsApi } from '../../api/operatingCostsApi';

interface UseOperatingCostsProps {
    buildingKey: string | undefined;
}

interface UseOperatingCostsReturn {
    costs: OperatingCost[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    totalYearlyCost: number;
    costsByCategory: { [categoryKey: string]: OperatingCost[] };
    addCost: (data: {
        categoryKey: string;
        amount: number;
        interval: CostInterval;
        description?: string;
        supplier?: string;
    }) => Promise<OperatingCost>;
    updateCost: (costKey: string, data: {
        categoryKey: string;
        amount: number;
        interval: CostInterval;
        description?: string;
        supplier?: string;
    }) => Promise<void>;
    deleteCost: (costKey: string) => Promise<void>;
    addAttachment: (costKey: string, attachment: FileAttachment) => Promise<void>;
    removeAttachment: (costKey: string, attachmentKey: string) => Promise<void>;
    refresh: () => Promise<void>;
    clearError: () => void;
}

export const useOperatingCosts = ({ buildingKey }: UseOperatingCostsProps): UseOperatingCostsReturn => {
    const [costs, setCosts] = useState<OperatingCost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Calculate total yearly cost
    const totalYearlyCost = useMemo(() => {
        return costs.reduce((total, cost) => {
            return total + calculateYearlyCost(cost.amount, cost.interval);
        }, 0);
    }, [costs]);

    // Group costs by category
    const costsByCategory = useMemo(() => {
        return costs.reduce((acc, cost) => {
            if (!acc[cost.categoryKey]) {
                acc[cost.categoryKey] = [];
            }
            acc[cost.categoryKey].push(cost);
            return acc;
        }, {} as { [categoryKey: string]: OperatingCost[] });
    }, [costs]);

    // Load costs
    const loadCosts = useCallback(async () => {
        if (!buildingKey) return;

        setLoading(true);
        setError(null);

        try {
            const loadedCosts = await operatingCostsApi.getCosts(buildingKey);
            // Sort by category and then by amount (descending)
            loadedCosts.sort((a, b) => {
                if (a.categoryKey !== b.categoryKey) {
                    return a.categoryKey.localeCompare(b.categoryKey);
                }
                return b.amount - a.amount;
            });
            setCosts(loadedCosts);
        } catch (err) {
            console.error('Error loading operating costs:', err);
            setError('Kunde inte ladda driftkostnader');
        } finally {
            setLoading(false);
        }
    }, [buildingKey]);

    // Load costs on mount or when building changes
    useEffect(() => {
        if (buildingKey) {
            loadCosts();
        }
    }, [buildingKey, loadCosts]);

    // Add new cost
    const addCost = useCallback(async (data: {
        categoryKey: string;
        amount: number;
        interval: CostInterval;
        description?: string;
        supplier?: string;
    }): Promise<OperatingCost> => {
        if (!buildingKey) {
            throw new Error('Ingen byggnad vald');
        }

        setSubmitting(true);
        setError(null);

        try {
            const newCost = await operatingCostsApi.createCost(buildingKey, data);
            await loadCosts();
            return newCost;
        } catch (err: any) {
            console.error('Error adding operating cost:', err);
            setError(err.message || 'Kunde inte lägga till driftkostnad');
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [buildingKey, loadCosts]);

    // Update cost
    const updateCost = useCallback(async (costKey: string, data: {
        categoryKey: string;
        amount: number;
        interval: CostInterval;
        description?: string;
        supplier?: string;
    }): Promise<void> => {
        if (!buildingKey) {
            throw new Error('Ingen byggnad vald');
        }

        setSubmitting(true);
        setError(null);

        try {
            await operatingCostsApi.updateCost(buildingKey, costKey, data);
            await loadCosts();
        } catch (err: any) {
            console.error('Error updating operating cost:', err);
            setError(err.message || 'Kunde inte uppdatera driftkostnad');
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [buildingKey, loadCosts]);

    // Delete cost
    const deleteCost = useCallback(async (costKey: string): Promise<void> => {
        if (!buildingKey) return;

        try {
            await operatingCostsApi.deleteCost(buildingKey, costKey);
            await loadCosts();
        } catch (err) {
            console.error('Error deleting operating cost:', err);
            setError('Kunde inte radera driftkostnad');
        }
    }, [buildingKey, loadCosts]);

    // Add attachment
    const addAttachment = useCallback(async (costKey: string, attachment: FileAttachment): Promise<void> => {
        if (!buildingKey) return;

        try {
            await operatingCostsApi.addAttachment(buildingKey, costKey, attachment);
            await loadCosts();
        } catch (err: any) {
            console.error('Error adding attachment:', err);
            setError(err.message || 'Kunde inte lägga till bilaga');
            throw err;
        }
    }, [buildingKey, loadCosts]);

    // Remove attachment
    const removeAttachment = useCallback(async (costKey: string, attachmentKey: string): Promise<void> => {
        if (!buildingKey) return;

        try {
            await operatingCostsApi.removeAttachment(buildingKey, costKey, attachmentKey);
            await loadCosts();
        } catch (err) {
            console.error('Error removing attachment:', err);
            setError('Kunde inte ta bort bilaga');
        }
    }, [buildingKey, loadCosts]);

    // Refresh
    const refresh = useCallback(async () => {
        await loadCosts();
    }, [loadCosts]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        costs,
        loading,
        error,
        submitting,
        totalYearlyCost,
        costsByCategory,
        addCost,
        updateCost,
        deleteCost,
        addAttachment,
        removeAttachment,
        refresh,
        clearError,
    };
};
